import React from 'react';
import type { FlowNodeData, FlowConnectionData, FlowValidationIssue, Condition } from '../../types/forms';

interface FlowConnectionProps {
  connection: FlowConnectionData;
  sourceNode: FlowNodeData;
  targetNode: FlowNodeData;
  isSelected: boolean;
  isLoop: boolean;
  isImplicit?: boolean;
  onSelect: (connectionId: string) => void;
}

function calculatePath(source: FlowNodeData, target: FlowNodeData): string {
  const startX = source.position.x + source.width;
  const startY = source.position.y + source.height / 2;
  const endX = target.position.x;
  const endY = target.position.y + target.height / 2;

  const dx = Math.abs(endX - startX);
  const controlOffset = Math.max(dx * 0.5, 50);

  return `M ${startX} ${startY} C ${startX + controlOffset} ${startY}, ${endX - controlOffset} ${endY}, ${endX} ${endY}`;
}

function getConditionLabel(condition?: Condition): string {
  if (!condition) return '';
  const opLabels: Record<string, string> = {
    equals: '=', not_equals: '≠', contains: 'contém',
    not_contains: 'não contém', is_empty: 'vazio',
    is_not_empty: 'preenchido', greater_than: '>', less_than: '<',
  };
  const op = opLabels[condition.operator] || condition.operator;
  const val = Array.isArray(condition.value)
    ? condition.value.join(', ')
    : condition.value || '';
  return val ? `${op} "${val}"` : op;
}

function getMidpoint(source: FlowNodeData, target: FlowNodeData): { x: number; y: number } {
  const startX = source.position.x + source.width;
  const startY = source.position.y + source.height / 2;
  const endX = target.position.x;
  const endY = target.position.y + target.height / 2;
  return { x: (startX + endX) / 2, y: (startY + endY) / 2 };
}

export const FlowConnection: React.FC<FlowConnectionProps> = ({
  connection,
  sourceNode,
  targetNode,
  isSelected,
  isLoop,
  isImplicit,
  onSelect,
}) => {
  const path = calculatePath(sourceNode, targetNode);
  const midpoint = getMidpoint(sourceNode, targetNode);

  // Implicit connections: lighter, non-interactive
  if (isImplicit) {
    return (
      <g>
        <path
          d={path}
          fill="none"
          stroke="#3f3f46"
          strokeWidth={1.5}
          strokeDasharray="4 4"
          opacity={0.6}
          markerEnd="url(#arrowhead-dim)"
          style={{ pointerEvents: 'none' }}
        />
      </g>
    );
  }

  const strokeColor = isLoop
    ? '#ef4444'
    : connection.isDefault
    ? '#525252'
    : isSelected
    ? '#818cf8'
    : '#6366f1';

  const dashArray = connection.isDefault || isLoop ? '6 4' : undefined;
  const strokeWidth = isSelected ? 3 : 2;

  const label = connection.isDefault
    ? 'Default'
    : getConditionLabel(connection.condition) || 'Configurar...';

  return (
    <g className="cursor-pointer" onClick={() => onSelect(connection.id)}>
      {/* Invisible wider path for easier clicking */}
      <path
        d={path}
        fill="none"
        stroke="transparent"
        strokeWidth={16}
        style={{ pointerEvents: 'stroke' }}
      />
      {/* Visible path */}
      <path
        d={path}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeDasharray={dashArray}
        markerEnd="url(#arrowhead)"
        className="transition-all duration-150"
        style={{ pointerEvents: 'none' }}
      />
      {/* Condition badge at midpoint */}
      <foreignObject
        x={midpoint.x - 60}
        y={midpoint.y - 12}
        width={120}
        height={24}
        style={{ pointerEvents: 'none' }}
      >
        <div className="flex justify-center">
          <span
            className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium truncate max-w-[110px] ${
              connection.isDefault
                ? 'bg-neutral-800 text-neutral-400 border border-neutral-700'
                : isSelected
                ? 'bg-primary-500/20 text-primary-300 border border-primary-500/40'
                : 'bg-neutral-900 text-neutral-300 border border-neutral-700'
            }`}
          >
            {label}
          </span>
        </div>
      </foreignObject>
    </g>
  );
};

// Ghost connection (during drag)
export const GhostConnection: React.FC<{
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}> = ({ startX, startY, endX, endY }) => {
  const dx = Math.abs(endX - startX);
  const controlOffset = Math.max(dx * 0.5, 50);
  const path = `M ${startX} ${startY} C ${startX + controlOffset} ${startY}, ${endX - controlOffset} ${endY}, ${endX} ${endY}`;

  return (
    <path
      d={path}
      fill="none"
      stroke="#6366f1"
      strokeWidth={2}
      strokeDasharray="6 4"
      opacity={0.6}
      style={{ pointerEvents: 'none' }}
    />
  );
};
